import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ArrowDownToLine,
  BarChart3,
  Bell,
  Building2,
  ChevronDown,
  ClipboardList,
  Coins,
  FileText,
  GitBranch,
  Home,
  Image as ImageIcon,
  LifeBuoy,
  MessageSquare,
  Percent,
  Receipt,
  Settings,
  ShieldCheck,
  UserCircle,
  Users,
  Wallet,
} from 'lucide-react';
import { BrandMark } from '@/components/common/BrandMark';
import { useAdminStats } from '@/hooks/api/usePortal';
import { cn } from '@/lib/utils';

interface SidebarProps {
  variant: 'customer' | 'admin';
}

interface NavItem {
  label: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  dotKey?: string;
}

interface NavGroup {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  children: NavItem[];
}

type NavEntry = NavItem | NavGroup;

function isGroup(entry: NavEntry): entry is NavGroup {
  return 'children' in entry;
}

export type { NavItem, NavGroup, NavEntry };
export { isGroup };

export const customerNav: NavItem[] = [
  { label: 'Overview', to: '/dashboard', icon: Home },
  { label: 'Wallet', to: '/dashboard/wallet', icon: Wallet },
  { label: 'Withdrawals', to: '/dashboard/withdrawals', icon: ArrowDownToLine },
  { label: 'Transactions', to: '/dashboard/transactions', icon: Receipt },
  { label: 'Referrals', to: '/dashboard/referrals', icon: Users },
  { label: 'Binary tree', to: '/dashboard/tree', icon: GitBranch },
  { label: 'Notifications', to: '/dashboard/notifications', icon: Bell },
  { label: 'Profile', to: '/dashboard/profile', icon: UserCircle },
  { label: 'Bank details', to: '/dashboard/bank', icon: Building2 },
  { label: 'Settings', to: '/dashboard/settings', icon: Settings },
  { label: 'Support', to: '/dashboard/support', icon: LifeBuoy },
];

export const adminNav: NavEntry[] = [
  { label: 'Overview', to: '/admin', icon: BarChart3 },
  { label: 'Users', to: '/admin/users', icon: Users },
  { label: 'Tree explorer', to: '/admin/tree', icon: GitBranch },
  { label: 'Manual payments', to: '/admin/payments', icon: ClipboardList, dotKey: 'pendingManualPayments' },
  { label: 'Cashback queue', to: '/admin/cashback', icon: Coins, dotKey: 'pendingCashback' },
  { label: 'Commissions', to: '/admin/commissions', icon: Percent, dotKey: 'pendingReferralCommissions' },
  { label: 'Withdrawals', to: '/admin/withdrawals', icon: ArrowDownToLine, dotKey: 'pendingWithdrawals' },
  { label: 'Plans', to: '/admin/plans', icon: Wallet },
  {
    label: 'Content',
    icon: ImageIcon,
    children: [
      { label: 'Banner', to: '/admin/media', icon: ImageIcon },
      { label: 'Leaders', to: '/admin/leaders', icon: ShieldCheck },
      { label: 'Chief guests', to: '/admin/chief-guests', icon: Users },
      { label: 'Licenses', to: '/admin/licenses', icon: FileText },
    ],
  },
  { label: 'Contact', to: '/admin/contact', icon: MessageSquare, dotKey: 'unreadMessages' },
  { label: 'Notifications', to: '/admin/notifications', icon: Bell, dotKey: 'unreadNotifications' },
  { label: 'Settings', to: '/admin/settings', icon: Settings },
  { label: 'Audit logs', to: '/admin/audit-logs', icon: ClipboardList },
  { label: 'Reports', to: '/admin/reports', icon: FileText },
];

function linkClass({ isActive }: { isActive: boolean }) {
  return cn(
    'relative flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-muted-foreground transition-all duration-200',
    'before:absolute before:left-0 before:top-1/2 before:h-4 before:w-0.5 before:-translate-y-1/2 before:rounded-full before:bg-gradient-to-b before:from-gold before:to-amber-400 before:opacity-0 before:shadow-sm before:shadow-gold/50 before:transition-opacity before:duration-200',
    'hover:bg-white/5 hover:text-foreground',
    isActive && 'bg-gradient-to-r from-gold/15 via-gold/5 to-transparent text-gold font-medium before:opacity-100',
  );
}

function SidebarLink({ item, counts }: { item: NavItem; counts?: Record<string, number> }) {
  const dotCount = item.dotKey ? counts?.[item.dotKey] ?? 0 : 0;
  return (
    <NavLink to={item.to} end={item.to === '/dashboard' || item.to === '/admin'} className={linkClass}>
      <item.icon className="h-4 w-4" />
      {item.label}
      {dotCount > 0 && (
        <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 font-mono text-[10px] font-bold text-primary-foreground">
          {dotCount > 99 ? '99+' : dotCount}
        </span>
      )}
    </NavLink>
  );
}

function SidebarGroup({ group }: { group: NavGroup }) {
  const location = useLocation();
  const hasActive = group.children.some((c) => location.pathname === c.to);
  const [open, setOpen] = useState(hasActive);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm text-muted-foreground transition-all duration-200',
          'hover:bg-white/5 hover:text-foreground',
          hasActive && 'text-gold font-medium',
        )}
      >
        <group.icon className="h-4 w-4" />
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronDown className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="ml-4 mt-0.5 space-y-0.5 border-l border-border pl-3">
          {group.children.map((child) => (
            <SidebarLink key={child.to} item={child} />
          ))}
        </div>
      )}
    </div>
  );
}

export function Sidebar({ variant }: SidebarProps) {
  const items = variant === 'admin' ? adminNav : customerNav;
  const { data: statsData } = useAdminStats();
  const counts = variant === 'admin' ? (statsData?.totals as Record<string, number> | undefined) : undefined;
  return (
    <aside
      className="relative hidden w-64 shrink-0 border-r border-border/60 bg-card/60 backdrop-blur-xl lg:flex lg:flex-col"
      data-testid={`sidebar-${variant}`}
    >
      <div className="flex h-16 items-center border-b border-border/50 px-6">
        <BrandMark />
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        {items.map((entry) =>
          isGroup(entry) ? (
            <SidebarGroup key={entry.label} group={entry} />
          ) : (
            <SidebarLink key={entry.to} item={entry} counts={counts} />
          ),
        )}
      </nav>
      <div className="relative border-t border-border/50 bg-gradient-to-t from-purple-900/20 to-transparent p-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/70">
          {variant === 'admin' ? 'Admin console' : 'Customer portal'}
        </span>
        <p className="mt-1 text-[11px] text-muted-foreground/70">
          DreamPick · Est. 2024
        </p>
      </div>
    </aside>
  );
}
